import * as React from "react";

// Interface for react-window List's scrollToRow method
interface VirtualListRef {
  scrollToRow?: (index: number, align?: "auto" | "center" | "end" | "smart" | "start") => void;
}

interface UseKeyboardNavigationProps {
  itemCount: number;
  focusedIndex: number;
  onFocusChange: (index: number) => void;
  onSelect: (index: number) => void;
  onClose: () => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  listRef?: React.RefObject<VirtualListRef | null>;
}

export function useKeyboardNavigation({
  itemCount,
  focusedIndex,
  onFocusChange,
  onSelect,
  onClose,
  searchInputRef,
  containerRef,
  isOpen,
  listRef,
}: UseKeyboardNavigationProps) {
  const scrollToRow = React.useCallback(
    (index: number) => {
      // Validate index first
      if (typeof index !== "number" || !Number.isFinite(index) || index < 0) {
        return;
      }

      // Use DOM scrolling - more reliable than react-window v2's scrollToRow
      // which can throw "Invalid index undefined" errors
      const container = containerRef.current;
      if (!container) return;

      const row = container.querySelector(`[data-row-index="${index}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    },
    [containerRef],
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Check if we're in an input
      const activeElement = document.activeElement;
      const isInInput = activeElement?.tagName === "INPUT";

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (focusedIndex < itemCount - 1) {
            const newIndex = focusedIndex + 1;
            onFocusChange(newIndex);
            scrollToRow(newIndex);
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (focusedIndex > 0) {
            const newIndex = focusedIndex - 1;
            onFocusChange(newIndex);
            scrollToRow(newIndex);
          }
          break;

        case "Enter":
        case " ":
          if (!isInInput && focusedIndex >= 0) {
            e.preventDefault();
            onSelect(focusedIndex);
          }
          break;

        case "Escape":
          e.preventDefault();
          if (isInInput) {
            (activeElement as HTMLInputElement).blur();
          } else {
            onClose();
          }
          break;

        case "/":
          if (!isInInput) {
            e.preventDefault();
            searchInputRef.current?.focus();
          }
          break;

        case "Home":
          if (!isInInput && itemCount > 0) {
            e.preventDefault();
            onFocusChange(0);
            scrollToRow(0);
          }
          break;

        case "End":
          if (!isInInput && itemCount > 0) {
            e.preventDefault();
            const lastIndex = itemCount - 1;
            onFocusChange(lastIndex);
            scrollToRow(lastIndex);
          }
          break;
      }
    },
    [isOpen, focusedIndex, itemCount, onFocusChange, onSelect, onClose, searchInputRef, scrollToRow],
  );

  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    scrollToRow,
  };
}
